import { ApolloClient, ApolloError } from '@apollo/client';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { DocumentNode } from 'graphql';

// Define the query args type
interface GraphQLQueryArgs {
  document: DocumentNode;
  variables?: Record<string, any>;
}

// Define the error type
interface GraphQLError {
  message: string;
  extensions?: Record<string, any>;
}

// Apollo base query function
export const apolloBaseQuery = (
  client?: ApolloClient<any>
): BaseQueryFn<GraphQLQueryArgs, unknown, GraphQLError> => {
  return async ({ document, variables }) => {
    try {
      // Use the provided client or get it from window (for client-side)
      const apolloClient = client ?? (window as any).__APOLLO_CLIENT__;

      if (!apolloClient) {
        throw new Error('Apollo Client not found');
      }

      // Check if it's a mutation or query based on the document
      const definition = document.definitions[0];
      if (definition && definition.kind === 'OperationDefinition') {
        if (definition.operation === 'mutation') {
          const result = await apolloClient.mutate({
            mutation: document,
            variables,
          });
          return { data: result.data };
        } else {
          const result = await apolloClient.query({
            query: document,
            variables,
            fetchPolicy: 'network-only', // Always fetch fresh data
          });
          return { data: result.data };
        }
      }

      throw new Error('Invalid GraphQL document');
    } catch (error) {
      if (error instanceof ApolloError) {
        return {
          error: {
            message: error.message,
            extensions: error.graphQLErrors[0]?.extensions,
          },
        };
      }

      return {
        error: {
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  };
};
